import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { ChevronLeft } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

// The fullscreen photo viewer renders on <body>, outside any dialog — while it's open, touches,
// focus and Escape inside it must not dismiss the dialog it was opened from. Same for a toast's
// button (e.g. Undo) tapped while a dialog is open.
const viewerOpen = () => typeof document !== "undefined" && !!document.querySelector("[data-fullscreen-viewer]");
const onToast = (e) => !!(e.detail?.originalEvent?.target || e.target)?.closest?.("[data-sonner-toaster]");
const keepOpenForViewer = (handler) => (e) => {
  if (viewerOpen() || onToast(e)) e.preventDefault();
  handler?.(e);
};

// Centered with a flex wrapper instead of translate(-50%,-50%): iOS Safari misplaces the text
// caret inside fixed elements that carry a CSS transform (WebKit bug 176896) — the cursor sat
// below the typed text in every dialog field on iPhone.
const DialogContent = React.forwardRef(({ className, children, onPointerDownOutside, onInteractOutside, onFocusOutside, onEscapeKeyDown, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "pointer-events-auto relative grid w-full max-w-lg gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg [&>*]:min-w-0",
          className
        )}
        onPointerDownOutside={keepOpenForViewer(onPointerDownOutside)}
        onInteractOutside={keepOpenForViewer(onInteractOutside)}
        onFocusOutside={keepOpenForViewer(onFocusOutside)}
        onEscapeKeyDown={keepOpenForViewer(onEscapeKeyDown)}
        {...props}>
        <DialogPrimitive.Close
          data-testid="dialog-back"
          className="inline-flex items-center gap-1 justify-self-end self-start -mr-2 -mt-1 px-3 min-h-[44px] rounded-sm text-sm font-semibold text-navy hover:text-terracotta transition-colors ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
          <ChevronLeft className="h-5 w-5" /> Back
          <span className="sr-only">Back</span>
        </DialogPrimitive.Close>
        {children}
      </DialogPrimitive.Content>
    </div>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
